import React from 'react';
import { pickBy } from 'lodash';
import Api from '../../../../api/Api';
import Form from '@rjsf/core';
import ObjectFieldTemplate from '../../../../components/form/ObjectFieldTemplate';
import OTButtonGroup from '../../../../components/tag/OTButtonGroup';
import OTButton from '../../../../components/tag/OTButton';
import { FormattedMessage, injectIntl } from 'react-intl';
import ModalWrapper from './ModalWrapper';
import { formType } from './ContactFormContainer';
import Alert from '../../../../components/Alert';
import FirstContactModalContent from './modals/FirstContactModalContent';
import CannotRemoveCloseContactModalContent from './modals/CannotRemoveCloseContactModalContent';
import RemoveContactModalContent from './modals/RemoveContactModalContent';
import { phoneRegex, postalCodeRegex } from '../../../../utils/Validation';
import { RELATION_VALUES } from '../../../../definitions/ContactConstants';

let { id: module_id } = module;

const transformErrorDefault = (error) => {
  if (error.name === 'required') {
    error.message = 'Kenttä on pakollinen';
  }
  if (error.name === 'minLength') {
    error.message =
      'Kentän vähimmäispituus on ' + error.params.limit + ' merkkiä';
  }
  if (error.name === 'maxLength') {
    error.message =
      'Kentän enimmäispituus on ' + error.params.limit + ' merkkiä';
  }
  return error;
};

const transformErrorsUpdateContact = (errors) => {
  return errors.map(transformErrorDefault).map((error) => {
    if (error.name === 'pattern' && error.property === '.postalCode') {
      error.message = 'Postinumeron on oltava viisinumeroinen';
    }
    if (error.name === 'pattern' && error.property === '.phone') {
      error.message =
        'Syöttämäsi numero on virheellinen. Syötä numero esim. muodossa 040-123456 tai +358 40 123456';
    }
    return error;
  });
};

/**
 * Return full name of contact for display purposese
 * if one of the names is defined, else return empty string
 *
 * @param formData
 * @returns {string}
 */
export const getFullName = (formData) => {
  return [formData.firstName, formData.lastName].filter(Boolean).join(' ');
};

const editContactSchema = (intl) => ({
  title: 'Muokkaa yhteyshenkilöä',
  type: 'object',
  required: ['relation', 'phone', 'firstName', 'lastName'],
  properties: {
    relation: {
      title: 'Suhteesi yhteyshenkilöön',
      type: 'string',
      enum: RELATION_VALUES,
      enumNames: RELATION_VALUES.map((value) =>
        intl.formatMessage({ id: `relation.${value}` })
      ),
    },
    firstName: { type: 'string', title: 'Etunimi' },
    lastName: { type: 'string', title: 'Sukunimi' },
    phone: {
      type: 'string',
      title: 'Puhelinnumero',
      pattern: phoneRegex.source,
    },
    postalCode: {
      type: 'string',
      title: 'Postinumero',
      pattern: postalCodeRegex.source,
    },
    postOffice: { type: 'string', title: 'Postitoimipaikka' },
    address: { type: 'string', title: 'Osoite' },
  },
});

const editContactUISchema = {
  postOffice: {
    'ui:readonly': true,
  },
  'ui:grid': [
    {
      className: 'row1',
      columns: [
        {
          field: 'relation',
          col: 6,
        },
      ],
    },
    {
      columns: [
        {
          col: 12,
          className: 'relative-info-label',
          title: 'Yhteyshenkilösi', // todo localize
        },
      ],
    },
    {
      className: 'relative-info',
      columns: [
        {
          col: 12,
          rows: [
            {
              columns: [
                {
                  field: 'firstName',
                  col: 6,
                },
                {
                  field: 'lastName',
                  col: 6,
                },
              ],
            },
            {
              columns: [
                {
                  field: 'phone',
                  col: 6,
                },
              ],
            },
            {
              columns: [
                {
                  field: 'postalCode',
                  col: 6,
                },
                {
                  field: 'postOffice',
                  col: 6,
                },
              ],
            },
            {
              columns: [
                {
                  field: 'address',
                  col: 12,
                },
              ],
            },
          ],
        },
      ],
    },
  ],
};

class EditContactForm extends React.Component {
  constructor(props) {
    super(props);
    // omit values which are null or empty string
    let formData = pickBy(props.contact, (val) => val !== null && val !== '');

    this.state = {
      formData,
      fetchingPostOffice: false,
      modal: null,
    };
  }

  /**
   * Check whether post office field should be emptied.
   * This should happen if postalCode field is emptied.
   *
   * @param previous previous state
   * @param next next state
   * @returns {boolean} whether post office should be fetched
   */
  checkShouldEmptyPostOffice = (previous, next) => {
    return !next.postalCode && next.postOffice;
  };

  /**
   * If postal code is filled (new value is 5 in length),
   * fetch post office
   *
   * @param previous previous state
   * @param next next state
   * @returns {boolean} whether post office should be fetched
   */
  checkShouldFetchPostOffice = (previous, next) => {
    if (!next.postalCode) {
      return false;
    }
    return (
      next.postalCode.length === 5 && next.postalCode !== previous.postalCode
    );
  };

  setFormData = (schema) => {
    if (this.checkShouldEmptyPostOffice(this.state.formData, schema.formData)) {
      console.log('should empty post office');
      this.setState({
        formData: {
          ...schema.formData,
          postOffice: undefined,
        },
      });
      return;
    }
    if (this.checkShouldFetchPostOffice(this.state.formData, schema.formData)) {
      this.setState({
        fetchingPostOffice: true,
      });
      Api.fetchPostOffice({ postalCode: schema.formData.postalCode })
        .then((postOffice) => {
          if (!postOffice) {
            postOffice = undefined;
          }
          this.setState({
            fetchingPostOffice: false,
            formData: {
              ...this.state.formData,
              postOffice,
            },
          });
        })
        .catch((error) => {
          this.setState({
            fetchingPostOffice: false,
          });
        });
    }

    this.setState({
      formData: schema.formData,
    });
  };

  validateFormData = (formData, errors) => {
    if (
      !this.state.fetchingPostOffice &&
      formData.postalCode &&
      formData.postalCode.length === 5 &&
      !formData.postOffice
    ) {
      errors.postOffice.addError(
        'Postitoimistoa ei löytynyt antamallasi postinumerolla.'
      );
    }

    if (!formData.firstName && !formData.lastName) {
      errors.firstName.addError('Lisää vähintään etu- tai sukunimi');
    }
    return errors;
  };

  submit = (schema, event) => {
    let { hasContacts } = this.props.formContext;
    if (!hasContacts) {
      this.setState({
        modal: {
          content: FirstContactModalContent,
          props: {
            name: getFullName(schema.formData),
            save: () =>
              this.updateContact({
                ...schema.formData,
                closestContact: true,
              }),
          },
        },
      });
    } else {
      this.updateContact({
        closestContact: false, // default, unless formData overrides
        ...schema.formData,
      });
    }
  };

  updateContact = (formData) => {
    let {
      actions,
      formContext: { setForm },
    } = this.props;
    actions.updateContact(formData).then(() => {
      setForm({ name: formType.LIST });
    });
  };

  startRemoveContactDialog = () => {
    let { formData } = this.state;
    let fullName = getFullName(formData);
    let modal = formData.closestContact
      ? {
          content: CannotRemoveCloseContactModalContent,
          props: {
            name: fullName,
          },
        }
      : {
          content: RemoveContactModalContent,
          props: {
            name: fullName,
            remove: () => this.removeContact(formData.id),
          },
        };
    this.setState({ modal });
  };

  removeContact = (contactId) => {
    let {
      actions,
      formContext: { setForm },
    } = this.props;
    actions.removeContact(contactId).then(() => {
      setForm({ name: formType.LIST });
    });
  };

  render() {
    let { intl, formContext, error, isFetching } = this.props;
    let { formData, modal } = this.state;

    return (
      <>
        <Form
          formData={formData}
          uiSchema={editContactUISchema}
          schema={editContactSchema(intl)}
          formContext={formContext}
          ObjectFieldTemplate={ObjectFieldTemplate}
          validate={this.validateFormData}
          onSubmit={this.submit}
          noHtml5Validate={true}
          onChange={(formData) => this.setFormData(formData)}
          showErrorList={false}
          transformErrors={transformErrorsUpdateContact}
        >
          {formContext.hasCloseContact && formData.closestContact && (
            <FormattedMessage id={`${module.id}.person.is.closestContact`} />
          )}
          <hr />
          {error && (
            <Alert type={'error'} message={`${module_id}.error.${error}`} />
          )}
          <div className={'clearfix'}>
            <div className={'pull-left'}>
              {
                /* only show remove option when editing, not when adding. */
                formContext.formName === formType.EDIT && (
                  <button
                    className={'edit-relative-button'}
                    onClick={(e) => {
                      e.preventDefault();
                      this.startRemoveContactDialog();
                    }}
                  >
                    <i className="ot-notification__icon icon-remove" />
                    <FormattedMessage id={`${module.id}.remove.contact`} />
                  </button>
                )
              }
            </div>

            <div className={'pull-right'}>
              <OTButtonGroup>
                <OTButton
                  color={'gray'}
                  onClick={(e) => {
                    e.preventDefault();
                    formContext.setForm({ name: formType.LIST });
                  }}
                >
                  <FormattedMessage id={'cancel'} />
                </OTButton>
                <OTButton color={'light'} type={'submit'} disabled={isFetching}>
                  <FormattedMessage id={'save'} />
                </OTButton>
              </OTButtonGroup>
            </div>
          </div>
        </Form>
        <ModalWrapper
          modal={modal}
          closeModal={() => this.setState({ modal: null })}
        />
      </>
    );
  }
}

export default injectIntl(EditContactForm);
